import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

// Connects to data-controller="dashboard-email-schedule-form"
//
// Drives the cadence builder. The two modes the user picks from map onto the two
// stored frequencies — "Every X weeks" is weekly (and covers daily at seven days
// a week), "Every X months" is monthly (and covers quarterly at three). This
// controller owns that mapping and which section is on screen, and nothing else
// about the schedule.
//
// The summary and next-send dates are NOT computed here: the controller pushes
// the current selection at the server, so the recurrence math has a single
// implementation in Ruby.
//
// The response is a turbo stream, not a frame, because two disjoint regions
// depend on the cadence: the summary card above the fields and the "Starting"
// picker below them. A frame can only replace one contiguous node, so driving
// both from a frame would mean two requests per edit.
export default class extends Controller {
  static targets = [
    "frequency",
    "intervalMonths",
    "intervalMonthsInput",
    "interval",
    "intervalSection",
    "monthsSection",
    "daysSection",
    "dayButton",
    "dayInputs",
    "everyDayButton",
    "monthlySection",
    "dayModeRadio",
    "dayOfMonth",
    "weekOfMonth",
    "weekday",
    "modeSelect",
    "submit",
    "startsOn",
    "sendMinutes",
    "timeZone",
  ];
  static values = { mode: String, previewUrl: String };

  declare readonly frequencyTarget: HTMLInputElement;
  declare readonly intervalMonthsTarget: HTMLInputElement;
  declare readonly intervalMonthsInputTarget: HTMLInputElement;
  declare readonly intervalTarget: HTMLInputElement;
  declare readonly intervalSectionTarget: HTMLElement;
  declare readonly monthsSectionTarget: HTMLElement;
  declare readonly daysSectionTarget: HTMLElement;
  declare readonly dayButtonTargets: HTMLButtonElement[];
  declare readonly dayInputsTarget: HTMLElement;
  declare readonly everyDayButtonTarget: HTMLButtonElement;
  declare readonly hasEveryDayButtonTarget: boolean;
  declare readonly monthlySectionTarget: HTMLElement;
  declare readonly dayModeRadioTargets: HTMLInputElement[];
  declare readonly dayOfMonthTarget: HTMLSelectElement;
  declare readonly weekOfMonthTarget: HTMLSelectElement;
  declare readonly weekdayTarget: HTMLSelectElement;
  declare readonly modeSelectTarget: HTMLSelectElement;
  declare readonly submitTarget: HTMLButtonElement;
  // Lives inside the preview frame and is absent while the cadence names no
  // day, so every read of it has to be guarded.
  declare readonly startsOnTarget: HTMLSelectElement;
  declare readonly hasStartsOnTarget: boolean;
  declare readonly sendMinutesTarget: HTMLSelectElement;
  declare readonly timeZoneTarget: HTMLSelectElement;

  declare modeValue: string;
  declare previewUrlValue: string;

  static MIN_INTERVAL = 1;
  static MAX_INTERVAL = 12;

  declare timeout: ReturnType<typeof setTimeout> | undefined;

  // The preview is a race: typing "12" fires a refresh for 1 and another for 12,
  // and nothing makes the server answer them in order. Without these the slower
  // first response lands last and puts the old cadence and dates back on screen.
  //
  // Two guards, because they cover different halves. The abort stops a
  // superseded request that has not answered yet; the token stops one that
  // already resolved and is waiting its turn on the microtask queue, which an
  // abort can no longer reach.
  private previewController: AbortController | undefined;
  private previewToken = 0;

  connect() {
    this.syncSubmitState();
    this.syncEveryDayState();
  }

  disconnect() {
    clearTimeout(this.timeout);
    this.previewController?.abort();
    // Nothing in flight may render into a card that is going away.
    this.previewToken += 1;
  }

  setMode() {
    const mode = this.modeSelectTarget.value;
    if (!mode || mode === this.modeValue) return;

    this.modeValue = mode;
    this.frequencyTarget.value = mode === "every_x_weeks" ? "weekly" : "monthly";

    // A weekly cadence with no day selected can never fire, so seed today.
    if (mode === "every_x_weeks" && this.selectedDays.length === 0) {
      this.setDaySelected(new Date().getDay(), true);
      this.writeDayInputs();
    }

    this.renderMode();
    this.refresh();
  }

  // The two monthly day pickers are radio-exclusive; the server clears whichever
  // one day_mode says is not in use.
  setDayMode() {
    this.refresh();
  }

  refreshFromInput() {
    this.refresh();
  }

  // Seven clicks otherwise, and an all-days weekly cadence is just "daily".
  selectEveryDay() {
    [0, 1, 2, 3, 4, 5, 6].forEach((wday) => this.setDaySelected(wday, true));
    this.writeDayInputs();
    this.refresh();
  }

  toggleDay(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const wday = Number(button.dataset.wday);
    this.setDaySelected(wday, button.getAttribute("aria-pressed") !== "true");
    this.writeDayInputs();
    this.refresh();
  }

  // Typed directly now. Clamped on the way through so a pasted 99 or a cleared
  // field cannot reach the preview, which would render a cadence the model then
  // rejects on save.
  refreshFromInterval() {
    this.interval = this.clampInterval(this.interval);
    this.refresh();
  }

  refreshFromMonths() {
    this.intervalMonths = this.clampInterval(this.intervalMonths);
    this.refresh();
  }

  private clampInterval(value: number): number {
    const bounds = this.constructor as typeof Controller & { MIN_INTERVAL: number; MAX_INTERVAL: number };
    return Math.min(bounds.MAX_INTERVAL, Math.max(bounds.MIN_INTERVAL, value));
  }

  private get interval(): number {
    return Number(this.intervalTarget.value) || 1;
  }

  private set interval(value: number) {
    this.intervalTarget.value = String(value);
  }

  private get dayMode(): string {
    return this.dayModeRadioTargets.find((radio) => radio.checked)?.value ?? "day_of_month";
  }

  // The visible field is the number input; intervalMonths is the hidden field
  // the form actually submits, so both are written.
  private get intervalMonths(): number {
    return Number(this.intervalMonthsInputTarget.value) || 1;
  }

  private set intervalMonths(value: number) {
    this.intervalMonthsTarget.value = String(value);
    this.intervalMonthsInputTarget.value = String(value);
  }

  private get selectedDays(): number[] {
    return this.dayButtonTargets
      .filter((button) => button.getAttribute("aria-pressed") === "true")
      .map((button) => Number(button.dataset.wday));
  }

  private setDaySelected(wday: number, selected: boolean) {
    const button = this.dayButtonTargets.find((candidate) => Number(candidate.dataset.wday) === wday);
    if (!button) return;

    button.setAttribute("aria-pressed", String(selected));
    // Same selected treatment as the referral service chips: a primary border
    // over a light primary wash, not a solid fill.
    button.classList.toggle("border-primary", selected);
    button.classList.toggle("bg-primary/10", selected);
    button.classList.toggle("text-primary", selected);
    button.classList.toggle("border-base-200", !selected);
    button.classList.toggle("bg-base-100", !selected);
    button.classList.toggle("text-base-content/80", !selected);
  }

  private writeDayInputs() {
    this.dayInputsTarget.innerHTML = "";
    this.selectedDays.forEach((wday) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "dashboard_email_schedule[days_of_week][]";
      input.value = String(wday);
      this.dayInputsTarget.appendChild(input);
    });
  }

  private renderMode() {
    // Keep the select in step when the mode is changed by anything other than
    // the select itself (initial render, or a reset).
    if (this.modeSelectTarget.value !== this.modeValue) {
      this.modeSelectTarget.value = this.modeValue;
    }

    // The two interval fields share the frequency row, so exactly one shows.
    const weekly = this.modeValue === "every_x_weeks";
    this.intervalSectionTarget.classList.toggle("hidden", !weekly);
    this.monthsSectionTarget.classList.toggle("hidden", weekly);
    this.daysSectionTarget.classList.toggle("hidden", !weekly);
    this.monthlySectionTarget.classList.toggle("hidden", weekly);
  }

  private refresh() {
    this.syncSubmitState();
    this.syncEveryDayState();

    // Marked as soon as the user acts, not when the request fires: the debounce
    // is 200ms and a local response can beat it, so waiting until loadPreview
    // shows the skeleton for a frame or not at all.
    this.previewCard?.classList.add("is-refreshing");

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.loadPreview(), 200);
  }

  private syncEveryDayState() {
    if (!this.hasEveryDayButtonTarget) return;

    this.everyDayButtonTarget.toggleAttribute("data-active", this.selectedDays.length === 7);
  }

  private syncSubmitState() {
    // Only the weekly mode can be left unfireable — the monthly pickers always
    // hold a value.
    const incomplete = this.modeValue === "every_x_weeks" && this.selectedDays.length === 0;
    this.submitTarget.disabled = incomplete;
  }

  // Kept as an action target for the sheet's own frame loads, which render the
  // card fresh — any skeleton left over from an in-flight edit comes off then.
  previewLoaded() {
    this.previewCard?.classList.remove("is-refreshing");
  }

  private get previewCard() {
    return document.getElementById("dashboard_email_schedule_preview");
  }

  private async loadPreview() {
    if (!this.previewCard) return;

    const url = new URL(this.previewUrlValue, document.baseURI);
    const param = (name: string, value: string) =>
      url.searchParams.set(`dashboard_email_schedule[${name}]`, value);

    param("frequency", this.frequencyTarget.value);
    param("interval_weeks", String(this.interval));
    param("interval_months", this.intervalMonthsTarget.value);
    param("day_mode", this.dayMode);
    param("day_of_month", this.dayOfMonthTarget.value);
    param("week_of_month", this.weekOfMonthTarget.value);
    param("weekday", this.weekdayTarget.value);
    // The preview's summary and its first-send date both depend on these — the
    // first send can be today when the send time is still ahead.
    param("send_minutes_after_midnight", this.sendMinutesTarget.value);
    param("time_zone", this.timeZoneTarget.value);
    // Round-trips the chosen start so the rebuilt picker comes back on it. The
    // server drops it when a cadence change has made it unreachable.
    if (this.hasStartsOnTarget) param("starts_on", this.startsOnTarget.value);
    this.selectedDays.forEach((wday) => {
      url.searchParams.append("dashboard_email_schedule[days_of_week][]", String(wday));
    });

    // Supersede whatever is still in flight. An aborted fetch rejects, which the
    // catch below swallows along with the offline case — both mean "no preview
    // from this request", and neither is worth saying.
    this.previewController?.abort();
    const controller = new AbortController();
    this.previewController = controller;
    const token = (this.previewToken += 1);

    // The skeleton comes off in `finally`: a failed refresh must not leave the
    // card pulsing forever over figures it never replaced. The catch is not
    // decoration — nothing awaits this call, so a rejection would surface as an
    // unhandled promise rejection rather than anything the user can act on.
    // Dropping the refresh leaves the last good figures on screen instead.
    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: controller.signal,
      });
      const body = response.ok ? await response.text() : null;
      // Re-checked after every await: a newer refresh can start while this one
      // is reading its body, and the last writer wins on screen.
      if (body !== null && token === this.previewToken) Turbo.renderStreamMessage(body);
    } catch {
      // Offline, superseded, or the sheet closed mid-flight. Nothing to say.
    } finally {
      // Only the newest request owns the skeleton. An older one clearing it
      // would stop the card pulsing while its replacement is still coming.
      if (token === this.previewToken) this.previewCard?.classList.remove("is-refreshing");
    }
  }
}
